import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { getPlanes, actualizarPlan, PlanDto } from '../../services/adminService';
import './PlanesPage.css';

const PlanesPage: React.FC = () => {
  const [planes, setPlanes] = useState<PlanDto[]>([]);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState<string | null>(null);
  const [form, setForm] = useState({ precioUsdMes: 0, maxEmpleados: 0, activo: true });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exito, setExito] = useState<string | null>(null);

  useEffect(() => {
    getPlanes().then(setPlanes).finally(() => setCargando(false));
  }, []);

  const iniciarEdicion = (plan: PlanDto) => {
    setEditando(plan.codigo);
    setForm({ precioUsdMes: plan.precioUsdMes, maxEmpleados: plan.maxEmpleados, activo: plan.activo });
    setError(null);
    setExito(null);
  };

  const cancelar = () => { setEditando(null); setError(null); };

  const guardar = async (codigo: string) => {
    setGuardando(true);
    setError(null);
    try {
      const updated = await actualizarPlan(codigo, form);
      setPlanes(prev => prev.map(p => p.codigo === codigo ? updated : p));
      setEditando(null);
      setExito(`Plan "${updated.nombre}" actualizado correctamente.`);
      setTimeout(() => setExito(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <AdminLayout>
      <div className="planes-page">
        <div className="planes-header">
          <h1 className="planes-title">Planes</h1>
          <p className="planes-subtitle">Gestioná los precios y límites de cada plan</p>
        </div>

        {exito && (
          <div className="planes-alert success"><i className="fas fa-check-circle"></i> {exito}</div>
        )}

        {cargando ? (
          <div className="planes-loading"><i className="fas fa-spinner fa-spin"></i> Cargando...</div>
        ) : (
          <div className="planes-grid">
            {planes.map(plan => (
              <div key={plan.codigo} className={`plan-card ${!plan.activo ? 'inactivo' : ''}`}>
                <div className="plan-card-header">
                  <div>
                    <h2 className="plan-nombre">{plan.nombre}</h2>
                    <span className="plan-codigo">{plan.codigo}</span>
                  </div>
                  <span className={`plan-estado ${plan.activo ? 'activo' : 'inactivo'}`}>
                    {plan.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </div>

                {editando === plan.codigo ? (
                  <div className="plan-edit-form">
                    {error && <div className="plan-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
                    <div className="plan-field">
                      <label>Precio (USD/mes)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.precioUsdMes}
                        onChange={e => setForm(f => ({ ...f, precioUsdMes: Number(e.target.value) }))}
                      />
                    </div>
                    <div className="plan-field">
                      <label>Máx. empleados</label>
                      <input
                        type="number"
                        min="1"
                        value={form.maxEmpleados}
                        onChange={e => setForm(f => ({ ...f, maxEmpleados: Number(e.target.value) }))}
                      />
                    </div>
                    <div className="plan-field plan-field-check">
                      <label>
                        <input
                          type="checkbox"
                          checked={form.activo}
                          onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))}
                        />
                        Plan activo (disponible para nuevas empresas)
                      </label>
                    </div>
                    <div className="plan-edit-actions">
                      <button className="plan-btn-cancel" onClick={cancelar}>Cancelar</button>
                      <button className="plan-btn-save" onClick={() => guardar(plan.codigo)} disabled={guardando}>
                        {guardando ? <><div className="plan-spinner"></div> Guardando</> : 'Guardar cambios'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="plan-info">
                    <div className="plan-precio">
                      <span className="plan-precio-valor">${plan.precioUsdMes}</span>
                      <span className="plan-precio-label">/mes USD</span>
                    </div>
                    <div className="plan-detalle">
                      <div className="plan-detalle-item">
                        <i className="fas fa-users"></i>
                        <span>Hasta <strong>{plan.maxEmpleados}</strong> empleados</span>
                      </div>
                    </div>
                    <button className="plan-btn-edit" onClick={() => iniciarEdicion(plan)}>
                      <i className="fas fa-pencil-alt"></i> Editar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PlanesPage;
